/**
 * /mcp/:slug — SEO landing page for each MCP server.
 *
 * Targets queries like:
 *   "Notion MCP server", "Linear MCP Claude Desktop", "how to use Notion MCP",
 *   "connect Notion to Claude", "GitHub MCP setup"
 *
 * Separate from /registry/:slug (community hub with stars/comments).
 * This page is purely editorial + conversion-focused.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Check, ChevronDown, Copy,
  KeyRound, Globe, Zap, BookOpen, ArrowLeft,
} from "lucide-react";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import SeoHead from "../components/SeoHead";
import { getMcpSeoBySlug, type McpSeoEntry } from "../data/mcpSeoData";
import { getMcpBySlug } from "../data/mcpRegistry";
import NotFound from "./notfound";

// ── Copy button ───────────────────────────────────────────────────────────────

const CopyButton = ({ text, label = "Copy" }: { text: string; label?: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.span key="c" initial={{ scale: 0.7 }} animate={{ scale: 1 }} exit={{ scale: 0.7 }} className="flex items-center gap-1.5 text-emerald-500">
            <Check className="w-3 h-3" /> Copied!
          </motion.span>
        ) : (
          <motion.span key="u" initial={{ scale: 0.7 }} animate={{ scale: 1 }} exit={{ scale: 0.7 }} className="flex items-center gap-1.5">
            <Copy className="w-3 h-3" /> {label}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

// ── FAQ accordion item ────────────────────────────────────────────────────────

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="font-medium text-foreground pr-4">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Auth badge ────────────────────────────────────────────────────────────────

const AuthBadge = ({ type }: { type: McpSeoEntry["authTypeName"] }) => {
  if (type === "None (public)") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
        <Globe className="w-3 h-3" />
        No auth required
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
      <KeyRound className="w-3 h-3" />
      {type}
    </span>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function McpLandingPage() {
  const { slug = "" } = useParams();
  const seo = getMcpSeoBySlug(slug);
  const registry = getMcpBySlug(slug);

  if (!seo) return <NotFound />;

  const endpointCount = registry?.endpointCount;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SeoHead
        title={seo.pageTitle}
        description={seo.metaDescription}
        canonical={`/mcp/${seo.slug}`}
      />
      <TopHeader />

      {/* ── Breadcrumb ── */}
      <div className="max-w-4xl mx-auto w-full px-4 pt-8 pb-0">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link to="/registry" className="hover:text-foreground transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" />
            MCP Registry
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">{seo.headline}</span>
        </nav>
      </div>

      {/* ── Hero ── */}
      <section className="max-w-4xl mx-auto w-full px-4 pt-10 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          {/* Category + auth badges */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
              {seo.category}
            </span>
            <AuthBadge type={seo.authTypeName} />
            {endpointCount ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20">
                <Zap className="w-3 h-3" />
                {endpointCount} endpoints
              </span>
            ) : null}
          </div>

          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-tight">
            {seo.headline}
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed">
            {seo.subheadline}
          </p>

          {/* CTA row */}
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/registry/${seo.slug}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
            >
              <Zap className="w-4 h-4" />
              Try in Registry
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition-colors"
            >
              Convert your own API
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ── About ── */}
      <section className="max-w-4xl mx-auto w-full px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="grid md:grid-cols-2 gap-8"
        >
          <div className="rounded-2xl border border-border bg-card p-8">
            <h2 className="text-lg font-bold mb-3">What is {seo.headline.replace(" Server", "")}?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{seo.about}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8">
            <h2 className="text-lg font-bold mb-3">Why use it as an MCP?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{seo.whyMcp}</p>
          </div>
        </motion.div>
      </section>

      {/* ── Use cases ── */}
      <section className="max-w-4xl mx-auto w-full px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="text-2xl font-bold mb-8">What can Claude do with {seo.headline.replace(" Server", "").replace(" MCP", "")}?</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {seo.useCases.map((uc, i) => (
              <motion.div
                key={uc.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="rounded-xl border border-border bg-card p-5"
              >
                <span className="text-2xl mb-3 block">{uc.icon}</span>
                <h3 className="font-semibold text-sm mb-1.5 text-foreground">{uc.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{uc.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Claude Desktop Setup ── */}
      <section className="max-w-4xl mx-auto w-full px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl border border-border bg-card p-8"
        >
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-bold mb-1">Connect to Claude Desktop</h2>
              <p className="text-sm text-muted-foreground">
                Add this to your Claude Desktop <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">claude_desktop_config.json</code>
              </p>
            </div>
            <AuthBadge type={seo.authTypeName} />
          </div>

          {/* Auth setup instructions */}
          {seo.authSetup && (
            <div className="mb-6 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                  {seo.authTypeName} setup
                </span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{seo.authSetup}</p>
            </div>
          )}

          {/* Config snippet */}
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono text-muted-foreground">claude_desktop_config.json</span>
              <CopyButton text={seo.claudeDesktopConfig} label="Copy config" />
            </div>
            <pre className="bg-zinc-950 dark:bg-zinc-900 text-zinc-100 text-xs leading-relaxed p-5 rounded-xl overflow-x-auto font-mono border border-zinc-800">
              {seo.claudeDesktopConfig}
            </pre>
          </div>

          {/* Steps */}
          <ol className="mt-6 space-y-2">
            {[
              "Open Claude Desktop → Settings → Developer → Edit Config",
              "Paste the JSON above (merge with existing entries if you have others)",
              seo.authSetup ? "Replace the placeholder token with your real credential" : "Save and restart Claude Desktop",
              seo.authSetup ? "Save and restart Claude Desktop" : null,
              "Ask Claude: \"What can you do with " + seo.headline.replace(" Server", "") + "?\"",
            ].filter(Boolean).map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </motion.div>
      </section>

      {/* ── FAQ ── */}
      <section className="max-w-4xl mx-auto w-full px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
          <div className="space-y-3">
            {seo.faq.map((item) => (
              <FaqItem key={item.q} {...item} />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Related MCPs ── */}
      {seo.related.length > 0 && (
        <section className="max-w-4xl mx-auto w-full px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h2 className="text-xl font-bold mb-5">Related MCP servers</h2>
            <div className="flex flex-wrap gap-3">
              {seo.related.map((relSlug) => {
                const rel = getMcpBySlug(relSlug);
                if (!rel) return null;
                return (
                  <Link
                    key={relSlug}
                    to={`/mcp/${relSlug}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-muted/40 transition-colors text-sm font-medium"
                  >
                    {rel.name}
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </Link>
                );
              })}
            </div>
          </motion.div>
        </section>
      )}

      {/* ── CTA strip ── */}
      <section className="max-w-4xl mx-auto w-full px-4 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 p-10 text-white text-center shadow-xl"
        >
          <h2 className="text-2xl font-bold mb-2">Have your own API?</h2>
          <p className="text-blue-100 mb-7 text-base">
            Convert any OpenAPI or REST spec into a live MCP server in minutes. No code required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-white text-blue-600 font-semibold hover:bg-blue-50 transition-colors shadow text-sm"
            >
              <Zap className="w-4 h-4" />
              Convert your API to MCP
            </Link>
            <Link
              to="/registry"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-white/15 border border-white/30 text-white font-semibold hover:bg-white/25 transition-colors text-sm"
            >
              <BookOpen className="w-4 h-4" />
              Browse all MCPs
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ── Structured data for Google ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": seo.headline,
            "description": seo.metaDescription,
            "url": `https://apitomcpconverter.com/mcp/${seo.slug}`,
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          }),
        }}
      />

      <Footer />
    </div>
  );
}
